/**
 * CESKY VOKATIV (5. pad) — JEDINE misto v REOS, kde se jmeno sklonuje.
 *
 * Puvodne zilo v `apps/web-admin/src/lib/greeting.ts` (krok UXR5 bod 5:
 * obrazovka zdravila „Dobré ráno, Richard" misto „Richarde"). Krok MRGC1 ho
 * presunul sem, protoze tentyz problem ma e-mail klientovi z CRM4 („Dobrý den,
 * Eva Dvořáková," — nalez N5 reportu CRM4) a backend do web-adminu nevidi.
 * Dve kopie pravidel sklonovani by se rozesly (CLAUDE.md §8.2), takze tady je
 * GRAMATIKA a v katalogu (`cs.json`) je VETA, do ktere se jmeno dosadi.
 *
 * ---------------------------------------------------------------------------
 * ROZHODNUTI UXR5-VOKATIV (plati dal) — pravidla, ne slovnik jmen
 *
 * Vokativ se tvori z koncovky, ne z tabulky krestnich jmen: tabulka by
 * fungovala jen pro jmena, ktera v ni nekdo vyjmenoval, a kazde dalsi
 * (cizi, slozene, zenske) by spadlo na 1. pad. Pravidla nize pokryvaji tvary,
 * ktere se v ceskych jmenech vyskytuji; co pod zadne nespada (jmena koncici
 * samohlaskou mimo -a), zustava BEZE ZMENY — „Ivo" je v 5. padu „Ivo"
 * a nechat jmeno v klidu je vzdy lepsi nez ho zkomolit.
 * ---------------------------------------------------------------------------
 */
/** Mekke souhlasky — po nich ma vokativ koncovku `-i` (Tomáš → Tomáši). */
const SOFT_ENDINGS = ['š', 'č', 'ž', 'ř', 'j', 'ť', 'ď', 'ň', 'c', 's'];
/** Zadni souhlasky — po nich koncovka `-u` (Vojtěch → Vojtěchu). */
const BACK_ENDINGS = ['h', 'ch', 'k', 'g'];
/** Samohlasky, ktere vokativ nemeni (Jiří → Jiří, Ivo → Ivo). */
const UNCHANGED_ENDINGS = ['i', 'í', 'y', 'ý', 'o', 'u', 'ú', 'ů', 'e', 'ě', 'é'];
/**
 * 5. pad JEDNOHO slova (krestni jmeno nebo muzske prijmeni — plati na obe
 * stejna pravidla: Novák → Nováku, Svoboda → Svobodo, Pokorný → Pokorný).
 * Prazdne jmeno vraci prazdny retezec — volajici pak pozdravi bez osloveni,
 * nikdy „Dobré ráno, undefined".
 */
export function vocativeCs(name) {
    const word = name.trim();
    if (word === '')
        return '';
    const lower = word.toLocaleLowerCase('cs-CZ');
    // Zenska jmena na -a: Jana → Jano, Kateřina → Kateřino (a muzska prijmeni
    // téhož tvaru: Svoboda → Svobodo, Procházka → Procházko).
    if (lower.endsWith('a'))
        return `${word.slice(0, -1)}o`;
    // Samohlasky (krome -a) vokativ nemeni.
    if (UNCHANGED_ENDINGS.some((end) => lower.endsWith(end)))
        return word;
    // -ek ztraci pohyblive „e": Marek → Marku, Radek → Radku, Čapek → Čapku.
    if (lower.endsWith('ek'))
        return `${word.slice(0, -2)}ku`;
    // Pavel → Pavle, Karel → Karle (pohyblive „e"), ale Daniel → Danieli:
    // po samohlasce „i" se „e" nevypousti.
    if (lower.endsWith('el'))
        return lower.endsWith('iel') ? `${word}i` : `${word.slice(0, -2)}le`;
    // Petr → Petře (vklada se „ř"), ostatni na -r berou „-e" nize.
    if (lower.endsWith('tr'))
        return `${word.slice(0, -1)}ře`;
    if (BACK_ENDINGS.some((end) => lower.endsWith(end)))
        return `${word}u`;
    if (SOFT_ENDINGS.some((end) => lower.endsWith(end)))
        return `${word}i`;
    // Tvrda souhlaska: Jan → Jane, Richard → Richarde, Martin → Martine.
    return `${word}e`;
}
/** Krestni jmeno z celeho jmena — prijmeni se neoslovuje. */
export function firstName(fullName) {
    return fullName.trim().split(/\s+/)[0] ?? '';
}
/**
 * OSLOVENI Z JMENA, KTERE ZADAL CLOVEK (makler do kontaktu, klient do
 * formulare). Formalni dopis klientovi oslovuje prijmenim („paní Dvořáková",
 * „pane Nováku"), ne krestnim jmenem.
 *
 * ROZHODNUTI MRGC1-OSLOVENI — jak se pozna rod, kdyz ho zadny sloupec nenese:
 *
 *  1. prijmeni na -á (Dvořáková, Malá, Bílá) = ZENA. Zenske prijmeni se ve
 *     vokativu nemeni, takze se dosadi, jak je.
 *  2. jinak rozhoduje krestni jmeno: koncovka -a (Eva, Jana, Petra) = ZENA.
 *     Diky tomu nespadne do „pane" ani cizi prijmeni bez ceske koncovky
 *     („Anna Schmidt").
 *  3. jinak MUZ: prijmeni se dava do vokativu (`vocativeCs`).
 *  4. JEDNO SLOVO (v kontaktu je casto jen krestni jmeno) honorifikum
 *     NEDOSTANE — „pane Evo" by bylo horsi nez neformalni „Evo". Vrati se
 *     `none` + vokativ toho slova.
 *
 * Prazdny vstup vraci `none` s prazdnym jmenem — volajici pozdravi bez
 * osloveni (`crm.viewing.mail.greeting`).
 */
export function salutationCs(fullName) {
    const words = fullName.trim().split(/\s+/).filter((w) => w !== '');
    const surname = words[words.length - 1];
    if (surname === undefined)
        return { honorific: 'none', name: '' };
    if (words.length === 1)
        return { honorific: 'none', name: vocativeCs(surname) };
    const lowerSurname = surname.toLocaleLowerCase('cs-CZ');
    const female = lowerSurname.endsWith('á') || (words[0] ?? '').toLocaleLowerCase('cs-CZ').endsWith('a');
    return female
        ? { honorific: 'madam', name: surname }
        : { honorific: 'sir', name: vocativeCs(surname) };
}
//# sourceMappingURL=vocative-cs.js.map